<template>
  <div
    id="dev-CheckoutSummary"
    class="checkout-summary"
  >
    <div class="summary-details">
      <!-- Highlighted Amount -->
      <div class="amount-prominent">
        <template v-if="campaign">
          <span class="amount-label free-time-label">
            <font-awesome-icon
              icon="fa-solid fa-bolt"
              class="free-icon"
            />
            <span class="free-text">
              <span v-text="campaignFreeTimeText"></span>
            </span>
          </span>
          <div class="amount-value free-amount">
            {{ $t('price_table.free') }}
          </div>
        </template>
        <template v-else>
          <span class="amount-label">
            {{ $t('checkout.amount_to_pay') }}
            <span v-if="minimumPeriodText"> {{ $t('checkout.for_period', { period: minimumPeriodText }) }}</span>
          </span>
          <div class="amount-value">
            {{ formattedAmount }}
          </div>
        </template>

        <a
          href="#"
          class="view-prices-link"
          @click.prevent="$emit('view-prices')"
        >
          {{ $t('checkout.view_other_prices') }}
        </a>
      </div>

      <div class="info-grid">
        <div class="info-item full-width">
          <span class="info-label">
            <font-awesome-icon
              icon="location-dot"
              class="me-1 text-muted"
            />
            {{ $t('checkout.point') }}
          </span>
          <span class="info-value">{{ pointName }}</span>
        </div>

        <div class="info-item border-right">
          <span class="info-label">
            <font-awesome-icon
              icon="plug"
              class="me-1 text-muted"
            />
            {{ $t('checkout.cable') }}
          </span>
          <div class="d-flex align-items-center justify-content-between">
            <span class="info-value">{{ cableTypeName }}</span>
            <BaseButton
              bare
              variant=""
              class="btn-change-payment"
              @click="$emit('change-cable')"
            >
              {{ $t('common.change') }}
            </BaseButton>
          </div>
        </div>

        <div class="info-item">
          <span class="info-label">
            <font-awesome-icon
              icon="credit-card"
              class="me-1 text-muted"
            />
            {{ $t('checkout.payment_method') }}
          </span>
          <div class="d-flex align-items-center justify-content-between">
            <span class="info-value">{{ paymentMethodName }}</span>
            <BaseButton
              bare
              variant=""
              class="btn-change-payment"
              @click="$emit('change-payment')"
            >
              {{ $t('common.change') }}
            </BaseButton>
          </div>
        </div>
      </div>

      <div
        class="detail-row deposit-row"
        :class="{ 'has-error': depositError && !depositAgreed }"
      >
        <label class="deposit-checkbox-label">
          <input
            v-model="depositAgreed"
            type="checkbox"
            class="deposit-checkbox"
          >
          <span class="checkbox-text">
            {{ $t('checkout.deposit_agreement') }}
          </span>
        </label>
        <span class="detail-value deposit-amount">{{ depositAmount }}</span>
      </div>

      <div
        v-if="depositError && !depositAgreed"
        class="deposit-error-msg"
      >
        <font-awesome-icon
          icon="circle-exclamation"
          class="me-1"
        />
        {{ depositError }}
      </div>

      <!-- Deposit Explanation -->
      <div class="deposit-explanation">
        <div class="explanation-content">
          <font-awesome-icon
            icon="circle-info"
            class="explanation-icon"
          />
          <div class="explanation-text">
            <strong>O que é a caução?</strong>
            <p>
              É uma reserva temporária de limite para garantir a segurança do equipamento. O valor é
              <strong>liberado automaticamente</strong> após a devolução em qualquer estação.
            </p>
          </div>
        </div>
      </div>

      <!-- Campaign / Sponsor Opt-in Card -->
      <div
        v-if="campaign && campaign.need_opt_in"
        class="promo-card"
        :class="{ 'is-active': optInAgreed }"
      >
        <div class="promo-header">
          <span class="promo-badge">
            <font-awesome-icon
              icon="bolt"
              class="me-1"
            />
            {{ $t('checkout.promo_benefit_badge') }}
          </span>
        </div>

        <label class="promo-checkbox-label">
          <input
            v-model="optInAgreed"
            type="checkbox"
            class="deposit-checkbox"
          >
          <span class="checkbox-text">
            {{ $t('home.take.campaign') }}
          </span>
        </label>

        <div
          v-if="optInAgreed"
          class="promo-status active"
        >
          <font-awesome-icon
            icon="circle-check"
            class="me-1"
          />
          {{ $t('checkout.promo_free_activated') }}
        </div>
        <div
          v-else
          class="promo-status warning"
        >
          <font-awesome-icon
            icon="triangle-exclamation"
            class="me-1"
          />
          {{ $t('checkout.promo_not_activated') }}
        </div>

        <div class="promo-footnote">
          {{ $t('checkout.promo_asterisk_note') }}
        </div>
      </div>
    </div>

    <!-- PIX Payment -->
    <div
      v-if="paymentMethod === 'pix'"
      class="pix-payment-section"
    >
      <!-- Unregistered PIX: require registration before generation -->
      <div
        v-if="!hasPixIdentification"
        class="pix-placeholder-content"
      >
        <div class="pix-info-card pix-info-card--warning">
          <font-awesome-icon
            icon="circle-exclamation"
            class="pix-icon"
          />
          <p class="pix-text">
            {{ $t('checkout.pix_registration_required') }}
          </p>
        </div>

        <div class="button-container button-group">
          <BaseButton
            variant="primary"
            block
            class="font-weight-bold"
            @click="$emit('change-payment')"
          >
            {{ $t('checkout.register_pix') }}
          </BaseButton>

          <BaseButton
            variant="outline-secondary"
            block
            class="font-weight-bold"
            @click="$emit('cancel')"
          >
            {{ $t('common.cancel') }}
          </BaseButton>
        </div>
      </div>

      <!-- Registered PIX: normal QR Code generation flow -->
      <div
        v-else
        class="pix-placeholder-content"
      >
        <div class="pix-info-card">
          <font-awesome-icon
            icon="qrcode"
            class="pix-icon"
          />
          <p class="pix-text">
            {{ $t('checkout.pix_generation_info') }}
          </p>
        </div>

        <!-- Payment confirmed: show Take Battery directly on this screen -->
        <div
          v-if="pixPaymentConfirmed"
          class="button-container button-group"
        >
          <div class="success-badge-inline mb-3">
            <font-awesome-icon
              icon="circle-check"
              class="me-2"
            />
            {{ $t('checkout.payment_confirmed') }}
          </div>
          <BaseButton
            variant="primary"
            block
            class="font-weight-bold"
            :disabled="isProcessing"
            @click="withdrawBatteryAfterPix"
          >
            <span
              v-if="isProcessing"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ isProcessing ? $t('checkout.processing') : $t('checkout.take_battery') }}
          </BaseButton>
          <BaseButton
            variant="outline-secondary"
            block
            class="font-weight-bold"
            :disabled="isProcessing"
            @click="$emit('cancel')"
          >
            {{ $t('common.cancel') }}
          </BaseButton>
        </div>

        <!-- Payment pending: show QR code button -->
        <div
          v-else
          class="button-container button-group"
        >
          <BaseButton
            variant="primary"
            block
            class="font-weight-bold"
            :class="{ 'btn-disabled-visual': !depositAgreed }"
            :disabled="isProcessing"
            @click="openPixModal"
          >
            {{ pixQrCode ? $t('pix_payment.view_pix') : $t('pix_payment.create_pix') }}
          </BaseButton>

          <BaseButton
            variant="outline-secondary"
            block
            class="font-weight-bold"
            @click="$emit('cancel')"
          >
            {{ $t('common.cancel') }}
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Pix Payment Modal -->
    <PixPaymentModal
      v-if="showPixModal"
      :qr-code="pixQrCode"
      :copy-paste-key="pixCopyPaste"
      :loading="isProcessing"
      :payment-confirmed="pixPaymentConfirmed"
      :is-processing="isProcessing"
      :pix-created-at="pixCreatedAt"
      :pix-expiry-minutes="pixExpiryMinutes"
      @close="closePixModal"
      @retry="generatePixQrCode"
      @take-battery="withdrawBatteryAfterPix"
      @expired="onPixExpired"
      @generate-new-qr="onGenerateNewPixQr"
    />

    <!-- Card Payment -->
    <div
      v-if="paymentMethod === 'card'"
      class="card-payment-section"
    >
      <p class="card-instruction">
        {{ $t('checkout.card_will_be_charged') }}
      </p>

      <div class="button-container button-group">
        <BaseButton
          variant="primary"
          block
          class="font-weight-bold"
          :class="{ 'btn-disabled-visual': !depositAgreed }"
          :disabled="isProcessing"
          @click="processCardPayment"
        >
          <span
            v-if="isProcessing"
            class="spinner-border spinner-border-sm me-2"
          ></span>
          <span class="btn-text-content">{{ buttonText() }}</span>
        </BaseButton>

        <BaseButton
          variant="outline-secondary"
          block
          class="font-weight-bold"
          :disabled="isProcessing"
          @click="$emit('cancel')"
        >
          {{ $t('common.cancel') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
  import API from '@/services/api'
  import getPlans from '@/views/home/service/getPlans'
  import getCustomerPlans from '@/views/home/service/getCustomerPlans'
  import { sessionRecorder, UserEvent, SESSION_EVENTS } from '@/services/sessionRecorder'
  import {
    CREDIT_CARD_RESERVATION_ALLOW,
    USER_INFO_SHARING_PERMISSION_GRANTED,
    USER_INFO_SHARING_PERMISSION_REFUSED,
    RETRY_TAKE_BATTERY_COUNT,
    ERRORS_TO_RETRY,
    RETRY_TAKE_BATTERY_INTERVAL,
    PAYMENT_METHOD_CREDIT_CARD,
    PAYMENT_METHOD_PIX
  } from '@/constants.js'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import PixPaymentModal from './PixPaymentModal.vue'
  import { getPlanSeconds } from '@/utils/planTime'

  const PIX_CHECKOUT_SESSION_KEY = 'pix_checkout'
  const PIX_CHECKOUT_EXPIRY_MINUTES = 10

  // Helper to get a consistent storage API: localStorage persists across F5 and
  // browser-kill (memory pressure), unlike sessionStorage which is wiped on kill.
  const pixCheckoutStorage = {
    get: () => localStorage.getItem(PIX_CHECKOUT_SESSION_KEY),
    set: value => localStorage.setItem(PIX_CHECKOUT_SESSION_KEY, value),
    remove: () => localStorage.removeItem(PIX_CHECKOUT_SESSION_KEY)
  }

  export default {
    name: 'CheckoutSummary',
    components: {
      BaseButton,
      PixPaymentModal
    },
    props: {
      transactionId: {
        type: Number,
        required: true
      },
      pointName: {
        type: String,
        required: true
      },
      token: {
        type: String,
        required: true
      },
      cableType: {
        type: Number,
        required: true
      },
      paymentMethod: {
        type: String,
        default: null,
        validator: value => value === null || ['pix', 'card'].includes(value)
      },
      amount: {
        type: [Number, String],
        required: true
      },
      user: {
        type: Object,
        required: true
      },
      pointId: {
        type: [Number, String],
        default: null
      }
    },
    emits: [
      'payment-success',
      'payment-error',
      'view-prices',
      'cancel',
      'user-updated',
      'change-cable',
      'change-payment',
      'pix-payment-authorized'
    ],
    data() {
      return {
        isProcessing: false,
        pixQrCode: null,
        pixCopyPaste: null,
        pixPaymentStatusInterval: null,
        pixPaymentId: null,
        pixPaymentConfirmed: false,
        retryTakeBatteryCount: RETRY_TAKE_BATTERY_COUNT,
        depositAgreed: false,
        depositError: '',
        optInAgreed: false,
        pointPlans: null,
        showPixModal: false,
        pixCreatedAt: null,
        abortController: null
      }
    },
    computed: {
      hasPixIdentification() {
        return Boolean(
          this.user?.pix_identification?.name &&
          this.user?.pix_identification?.cpf_or_cnpj
        )
      },
      pixExpiryMinutes() {
        return PIX_CHECKOUT_EXPIRY_MINUTES
      },
      campaign() {
        if (this.pointPlans?.campaign && this.pointPlans.campaign.free_time >= 300) {
          return this.pointPlans.campaign
        }
        return null
      },
      campaignFreeTimeText() {
        if (!this.campaign) return ''
        const totalSeconds = Math.floor(this.campaign.free_time)
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        const parts = []
        if (hours > 0) parts.push(`${hours}${this.$t('price_table.hours')}`)
        if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`)
        if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`)

        let timeString = ''
        if (parts.length === 0) {
          timeString = '0 minutos'
        } else if (parts.length === 1) {
          timeString = parts[0]
        } else {
          const lastPart = parts.pop()
          timeString = parts.join(', ') + ' e ' + lastPart
        }

        return `${this.$t('price_table.until')} ${timeString}: `
      },
      minimumPeriodText() {
        if (!this.pointPlans) return ''

        const hourly = this.pointPlans.price?.find(p => p.internal_ref === 'four_hours')
        const daily = this.pointPlans.price?.find(p => p.internal_ref === 'daily')
        const plan = hourly || daily

        if (plan) {
          const totalSeconds = getPlanSeconds(plan)
          return this.formatDurationNoPrefix(totalSeconds)
        }

        return ''
      },
      cableTypeName() {
        const names = {
          0: this.$t('cable_type.options.lightning.labelName'),
          1: this.$t('cable_type.options.micro.labelName'),
          2: this.$t('cable_type.options.usbc.labelName')
        }
        return names[this.cableType] || 'Unknown'
      },

      paymentMethodName() {
        if (this.paymentMethod === 'pix') {
          return 'PIX'
        }

        const card = this.selectedCard
        if (card && card.number) {
          const lastFour = card.number.slice(-4)
          let brand = card.flag || ''

          // Capitalize brand name nicely
          if (brand) {
            brand = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
          } else {
            brand = 'Cartão'
          }

          return `${brand} •••• ${lastFour}`
        }

        return this.$t('checkout.credit_card')
      },

      selectedCard() {
        if (!this.user) return null
        return (
          this.user.default_card ||
          (this.user.credit_cards && this.user.credit_cards.length > 0 ? this.user.credit_cards[0] : null)
        )
      },

      paymentIcon() {
        return this.paymentMethod === 'pix' ? 'fa-brands fa-pix' : 'credit-card'
      },

      formattedAmount() {
        const amount = this.amount

        // Handle object with reais and centavos properties
        if (typeof amount === 'object' && amount !== null) {
          const reais = parseInt(amount.reais || 0)
          const centavos = parseInt(amount.centavos || 0)
          return (reais + centavos / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          })
        }

        // Handle numeric value
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
        return (numAmount || 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        })
      },

      depositAmount() {
        if (!this.pointPlans) return 'R$ 99,90'

        const method = this.paymentMethod
        let depositData = null

        if (method === 'pix') {
          depositData = this.pointPlans.pix_security_deposit?.security_deposit
        } else {
          depositData = this.pointPlans.credit_card_security_deposit?.security_deposit
        }

        if (!depositData) {
          return 'R$ 99,90'
        }

        const reais = parseInt(depositData.reais || 0)
        const centavos = parseInt(depositData.centavos || 0)
        return (reais + centavos / 100).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        })
      }
    },
    watch: {
      depositAgreed(val) {
        if (val) {
          this.depositError = ''
        } else {
          // If they uncheck it, we should probably clear the PIX code
          // to reinforce that they can't proceed
          this.pixQrCode = null
          this.pixCopyPaste = null
          if (this.pixPaymentStatusInterval) {
            clearInterval(this.pixPaymentStatusInterval)
          }
        }
      }
    },
    mounted() {
      // Fetch point plans for pricing summary
      if (this.pointId) {
        this.fetchPointPlans()
      }
      this._restorePixFromSession()
    },
    beforeUnmount() {
      // Clear polling interval when component is destroyed
      if (this.pixPaymentStatusInterval) {
        clearInterval(this.pixPaymentStatusInterval)
      }
    },
    methods: {
      formatDurationNoPrefix(totalSeconds) {
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        const parts = []
        if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hora' : this.$t('price_table.hours')}`)
        if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`)
        if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`)

        if (parts.length === 0) {
          return '0 minutos'
        } else if (parts.length === 1) {
          return parts[0]
        } else {
          const lastPart = parts.pop()
          return parts.join(', ') + ' e ' + lastPart
        }
      },
      buttonText() {
        if (this.campaign && this.campaign.need_opt_in && this.optInAgreed) {
          return this.isProcessing ? this.$t('checkout.processing') : this.$t('checkout.confirm_free_withdrawal')
        } else if (this.campaign && this.campaign.need_opt_in && !this.optInAgreed) {
          return this.isProcessing
            ? this.$t('checkout.processing')
            : this.$t('checkout.confirm_withdrawal_without_free_time')
        } else {
          return this.isProcessing ? this.$t('checkout.processing') : this.$t('checkout.confirm_withdrawal')
        }
      },
      async fetchPointPlans() {
        try {
          const token = localStorage?.getItem('token') || null
          if (token) {
            this.pointPlans = await getCustomerPlans(this.pointId)
          } else {
            this.pointPlans = await getPlans(this.pointId, false)
          }
        } catch (error) {
          console.error('Error fetching point plans:', error)
        }
      },
      async generatePixQrCode() {
        if (!this.hasPixIdentification) {
          this.$emit('change-payment')
          return
        }

        if (!this.depositAgreed) {
          this.depositError =
            this.$t('checkout.deposit_agreement_required') || 'Você deve aceitar a caução para continuar'
          return
        }

        this.pixQrCode = null
        this.pixCopyPaste = null
        this.pixPaymentConfirmed = false
        this.isProcessing = true

        this.abortController = new AbortController()

        try {
          // Call API to generate PIX QR code
          const response = await API.post(
            'payment/pix',
            {
              transaction_id: this.transactionId,
              amount: this.amount,
              switch_payment_method: true // Signal the API that we are explicitly switching/refreshing
            },
            {
              signal: this.abortController.signal
            }
          )

          if (response.data && response.data.success) {
            if (response.data.qr_code) {
              const qrCodeImage = response.data.qr_code.image
              if (qrCodeImage) {
                if (!qrCodeImage.startsWith('data:')) {
                  this.pixQrCode = `data:image/png;base64,${qrCodeImage}`
                } else {
                  this.pixQrCode = qrCodeImage
                }
              }
              this.pixCopyPaste = response.data.qr_code.code
              this.pixPaymentId = response.data.payment_id || response.data.transaction_id
            }

            this.isProcessing = false

            // Set creation timestamp for countdown
            this.pixCreatedAt = Date.now()

            // Start polling for payment status
            this.startPixPaymentPolling()

            // Track PIX generated event
            sessionRecorder.addUserEvent(
              new UserEvent(SESSION_EVENTS.PIX_QRCODE_GENERATED, this.transactionId, { step: 'checkout' })
            )

            // Persist PIX state so the page can be restored after F5/reload
            pixCheckoutStorage.set(
              JSON.stringify({
                pixPaymentId: this.pixPaymentId,
                pixQrCode: this.pixQrCode,
                pixCopyPaste: this.pixCopyPaste,
                transactionId: this.transactionId,
                token: this.token,
                pointName: this.pointName,
                pointId: this.pointId,
                cableType: this.cableType,
                amount: this.amount,
                pixCreatedAt: this.pixCreatedAt
              })
            )
          } else {
            throw new Error(response.data?.error || 'Invalid response from PIX API')
          }
        } catch (error) {
          if (error.name === 'CanceledError' || error.name === 'AbortError') {
            console.log('Request was aborted, ignoring error')
            return
          }
          console.error('Error generating PIX QR code:', error)
          this.isProcessing = false
          this.$emit('payment-error', {
            error: 'Erro ao gerar QR Code PIX',
            details: error.message
          })
        } finally {
          this.abortController = null
        }
      },

      startPixPaymentPolling() {
        if (!this.pixPaymentId) return
        if (this.pixPaymentConfirmed) return
        if (this.pixPaymentStatusInterval) {
          clearInterval(this.pixPaymentStatusInterval)
          this.pixPaymentStatusInterval = null
        }
        this.pixPaymentStatusInterval = setInterval(async () => {
          if (this.pixPaymentConfirmed) {
            clearInterval(this.pixPaymentStatusInterval)
            this.pixPaymentStatusInterval = null
            return
          }
          try {
            const response = await API.get(`payment/status?pre_payment=${this.pixPaymentId}`)
            if (response.data && response.data.success && response.data.status === 2) {
              // APPROVED = 2
              clearInterval(this.pixPaymentStatusInterval)
              this.pixPaymentStatusInterval = null
              this.pixPaymentConfirmed = true
              this.isProcessing = false
              // Update stored entry to confirmed state so F5 after confirmation can restore it
              try {
                const stored = pixCheckoutStorage.get()
                if (stored) {
                  pixCheckoutStorage.set(JSON.stringify({ ...JSON.parse(stored), pixConfirmed: true }))
                }
              } catch {
                /* ignore */
              }
              this.$emit('pix-payment-authorized')
            }
          } catch (error) {
            console.warn('Error checking PIX payment status:', error)
          }
        }, 3000)
      },

      async withdrawBatteryAfterPix() {
        this.isProcessing = true
        try {
          // 1. Send transaction permission first
          await this.sendPermission()

          // 2. Then proceed to take the battery
          await this.takeBattery(PAYMENT_METHOD_PIX)

          // Clear PIX state on success
          pixCheckoutStorage.remove()
        } catch (error) {
          console.error('Error in PIX withdrawal flow:', error)
          this.isProcessing = false
          this.$emit('payment-error', error.message || 'Erro ao processar retirada')
        }
      },

      async processCardPayment() {
        if (!this.depositAgreed) {
          this.depositError = ''
          this.$nextTick(() => {
            this.depositError =
              this.$t('checkout.deposit_agreement_required') || 'Você deve aceitar a caução para continuar'
          })
          return
        }

        this.isProcessing = true

        try {
          // 1. Send transaction permission first
          await this.sendPermission()

          // 2. Get card ID
          const userToUse = this.user
          if (!userToUse.default_card && (!userToUse.credit_cards || userToUse.credit_cards.length === 0)) {
            throw new Error('No credit card found')
          }
          const cardId = userToUse.default_card ? userToUse.default_card.id : userToUse.credit_cards[0].id

          // 3. Then proceed to take the battery
          await this.takeBattery(PAYMENT_METHOD_CREDIT_CARD, cardId)
        } catch (error) {
          console.error('Error in card payment flow:', error)
          this.isProcessing = false
          this.$emit('payment-error', error.message || 'Erro ao processar retirada')
        }
      },

      async sendPermission() {
        let params = {
          transaction_permission_granted: CREDIT_CARD_RESERVATION_ALLOW,
          transaction_id: this.transactionId
        }

        // Check for campaign opt-in sharing
        const campaign = this.pointPlans?.campaign
        if (campaign && campaign.need_opt_in) {
          params['info_sharing_permission_granted'] = this.optInAgreed
            ? USER_INFO_SHARING_PERMISSION_GRANTED
            : USER_INFO_SHARING_PERMISSION_REFUSED
        }

        return API.post('transaction/permission', params)
      },

      async takeBattery(paymentMethod, cardId = null) {
        const payload = {
          transaction_id: this.transactionId,
          cable_type: this.cableType,
          payment_method: paymentMethod,
          amount: this.amount
        }

        if (cardId) {
          payload.card_id = cardId
        }

        try {
          const response = await API.post('transaction/validation/take_battery', payload)

          if (response.data && response.data.success) {
            this.retryTakeBatteryCount = RETRY_TAKE_BATTERY_COUNT
            this.$emit('payment-success', {
              transactionId: this.transactionId,
              paymentMethod: paymentMethod === PAYMENT_METHOD_PIX ? 'pix' : 'card',
              amount: this.amount,
              selectedSlot: response.data.selected_slot,
              terminal: response.data.terminal
            })
          } else if (this.retryTakeBatteryCount > 0 && ERRORS_TO_RETRY.includes(response?.data?.code)) {
            this.retryTakeBatteryCount--
            console.log(`[CheckoutSummary] Retrying take battery. Attempts remaining: ${this.retryTakeBatteryCount}`)

            setTimeout(async () => {
              return await this.takeBattery(paymentMethod, cardId)
            }, RETRY_TAKE_BATTERY_INTERVAL)
          } else {
            this.retryTakeBatteryCount = RETRY_TAKE_BATTERY_COUNT
            throw new Error(response.data?.error || 'Payment/Rental failed')
          }
        } catch (error) {
          // Only throw if we're not retrying
          if (!(
            this.retryTakeBatteryCount > 0 &&
            error.response &&
            ERRORS_TO_RETRY.includes(error.response.data?.code)
          )) {
            this.retryTakeBatteryCount = RETRY_TAKE_BATTERY_COUNT
            throw error
          }
        }
      },

      openPixModal() {
        if (!this.hasPixIdentification) {
          this.$emit('change-payment')
          return
        }

        if (!this.depositAgreed) {
          this.depositError = ''
          this.$nextTick(() => {
            this.depositError =
              this.$t('checkout.deposit_agreement_required') || 'Você deve aceitar a caução para continuar'
          })
          return
        }
        this.showPixModal = true
        if (!this.pixQrCode && !this.isProcessing) {
          this.generatePixQrCode()
        }
      },

      closePixModal() {
        this.showPixModal = false
        if (!this.pixPaymentConfirmed) {
          // User is cancelling — clear everything
          pixCheckoutStorage.remove()
          if (this.pixPaymentStatusInterval) {
            clearInterval(this.pixPaymentStatusInterval)
            this.pixPaymentStatusInterval = null
          }
          sessionRecorder.addUserEvent(
            new UserEvent(SESSION_EVENTS.PIX_PAYMENT_CANCELED, this.transactionId, { step: 'checkout' })
          )
        }
        // If confirmed, keep localStorage so F5 can restore the confirmed state
      },

      onPixExpired() {
        // QR Code expired — stop polling and clear storage
        if (this.pixPaymentStatusInterval) {
          clearInterval(this.pixPaymentStatusInterval)
          this.pixPaymentStatusInterval = null
        }
        pixCheckoutStorage.remove()
        sessionRecorder.addUserEvent(
          new UserEvent(SESSION_EVENTS.PIX_PAYMENT_EXPIRED, this.transactionId, { step: 'checkout' })
        )
      },

      onGenerateNewPixQr() {
        // User wants a new QR Code after expiry — reset and regenerate
        // pixCreatedAt is set by generatePixQrCode() on success to avoid double-reset
        // and prevent an invalid timestamp if the API call fails
        this.generatePixQrCode()
      },

      _restorePixFromSession() {
        try {
          const stored = pixCheckoutStorage.get()
          if (!stored) return
          const data = JSON.parse(stored)
          const elapsedMinutes = (Date.now() - data.pixCreatedAt) / 60000
          if (elapsedMinutes >= PIX_CHECKOUT_EXPIRY_MINUTES) {
            pixCheckoutStorage.remove()
            return
          }
          if (data.transactionId !== this.transactionId) return
          this.pixPaymentId = data.pixPaymentId
          this.pixQrCode = data.pixQrCode
          this.pixCopyPaste = data.pixCopyPaste
          this.pixCreatedAt = data.pixCreatedAt
          this.depositAgreed = true
          if (data.pixConfirmed) {
            // Payment was already confirmed before the reload — skip polling
            this.pixPaymentConfirmed = true
          } else {
            this.startPixPaymentPolling()
          }
          this.showPixModal = true
        } catch {
          pixCheckoutStorage.remove()
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .checkout-summary {
    padding: 0.5rem 0.25rem;
    background: transparent;
    border-radius: 16px;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }

  .summary-details {
    background: #ffffff;
    border-radius: 16px;
    padding: 1rem 0; // Padding only on top/bottom
    margin-bottom: 1rem;
    border: 1px solid #f3f4f6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    overflow: hidden;
    position: relative; // Better for layout stability
  }

  .amount-prominent {
    text-align: center;
    padding-bottom: 1rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid #f3f4f6;

    .amount-label {
      font-size: 0.75rem;
      color: #6b7280;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .amount-value {
      font-size: 2.25rem;
      font-weight: 900;
      color: #f58a48;
      margin: 0.25rem 0;
      line-height: 1;
    }

    .view-prices-link {
      font-size: 0.75rem;
      color: #3b82f6;
      font-weight: 700;
      text-decoration: none;
      border-bottom: 1px solid currentColor;

      &:hover {
        color: #2563eb;
      }
    }
  }

  .info-grid {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 0.5rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    padding: 0.75rem 1.25rem;
    border-bottom: 1px solid #f3f4f6;
    gap: 0.15rem;

    &:last-child {
      border-bottom: none;
    }
  }

  .info-label {
    font-size: 0.7rem;
    color: #9ca3af;
    font-weight: 700;
    text-transform: uppercase;
    display: flex;
    align-items: center;
  }

  .info-value {
    font-size: 0.9rem;
    color: #111827;
    font-weight: 700;
    line-height: 1.2;
    word-break: break-word;
  }

  .btn-change-payment {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #3b82f6;
    font-size: var(--fs-xs);
    font-weight: var(--fw-semibold);
    letter-spacing: -0.01em;
    text-transform: uppercase;
    padding: 0.25rem 0.6rem;
    cursor: pointer;
    border-radius: 6px;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      background: #f1f5f9;
      color: #2563eb;
    }
  }

  .free-time-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background-color: #fff7ed;
    border-radius: 8px;
    padding: 4px 10px;
    margin-bottom: 4px;
  }

  .free-amount {
    color: #16a34a !important;
  }

  .free-icon {
    color: #f97316;
  }

  .free-text {
    color: #9a3412;
    font-weight: 600;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
  }

  // Deposit Section
  .detail-row.deposit-row {
    margin: 0 1rem;
    padding: 0.6rem 0.85rem;
    background: #fdf4ed;
    border-radius: 10px;
    border: 1px solid #fce4d6;
    transition: all 0.2s ease;

    &.has-error {
      background: #fef2f2;
      border-color: #fecaca;
      animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    }

    .deposit-checkbox-label {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      cursor: pointer;
      margin: 0;
      flex: 1;
    }

    .deposit-checkbox {
      width: 1rem;
      height: 1rem;
      accent-color: #f58a48;
      cursor: pointer;
    }

    .checkbox-text {
      font-size: 0.8rem;
      color: #424242;
      font-weight: 700;
    }

    .detail-value.deposit-amount {
      color: #f58a48;
      font-weight: 800;
      font-size: 0.95rem;
    }
  }

  .deposit-explanation {
    margin: 0.75rem 1rem 0 1rem;
    padding: 0.75rem;
    background-color: #f8fafc;
    border-radius: 10px;
    border: 1px solid #e2e8f0;

    .explanation-content {
      display: flex;
      gap: 0.6rem;
      align-items: flex-start;
    }

    .explanation-icon {
      color: #64748b;
      font-size: 0.9rem;
      margin-top: 0.15rem;
    }

    .explanation-text {
      strong {
        display: block;
        font-size: 0.75rem;
        color: #334155;
        margin-bottom: 0.15rem;
      }

      p {
        font-size: 0.75rem;
        color: #64748b;
        margin: 0;
        line-height: 1.3;

        strong {
          display: inline;
          color: #f58a48;
          font-weight: 700;
        }
      }
    }
  }

  .deposit-error-msg {
    color: #f3553d;
    font-size: 0.75rem;
    font-weight: 700;
    text-align: center;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }

  // Promo / Campaign Card
  .promo-card {
    margin: 0.75rem 1rem 0;
    padding: 0.75rem 0.85rem;
    border-radius: 10px;
    background: #fffbeb;
    border: 1px solid #fed7aa;
    transition: all 0.25s ease;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &.is-active {
      background: #f0fdf4;
      border-color: #bbf7d0;
    }

    .promo-header {
      display: flex;
      align-items: center;
    }

    .promo-badge {
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      padding: 0.2rem 0.5rem;
      border-radius: 6px;
      background: #fef3c7;
      color: #b45309;
      display: inline-flex;
      align-items: center;
      transition: all 0.25s ease;
    }

    &.is-active .promo-badge {
      background: #dcfce7;
      color: #15803d;
    }

    .promo-checkbox-label {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      cursor: pointer;
      margin: 0;

      .checkbox-text {
        font-size: 0.82rem;
        font-weight: 600;
        color: #1f2937;
        line-height: 1.35;
      }
    }

    .promo-status {
      font-size: 0.75rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      padding-left: 1.6rem;

      &.active {
        color: #16a34a;
      }

      &.warning {
        color: #d97706;
      }
    }

    .promo-footnote {
      font-size: 0.7rem;
      color: #64748b;
      line-height: 1.3;
      padding-top: 0.35rem;
      border-top: 1px dashed #e2e8f0;
      margin-top: 0.15rem;
    }
  }

  // PIX Payment Section
  .pix-payment-section {
    margin-top: 1rem;
  }

  .pix-placeholder-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .success-badge-inline {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #d1fae5;
    color: #065f46;
    border-radius: 8px;
    padding: 0.6rem 1rem;
    font-weight: 600;
    font-size: 0.9rem;
  }

  .pix-info-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;

    .pix-icon {
      font-size: 2.5rem;
      color: #f58a48;
      opacity: 0.8;
    }

    .pix-text {
      font-size: 0.9rem;
      color: #475569;
      font-weight: 600;
      line-height: 1.4;
      margin: 0;
    }

    &--warning {
      background: #fffbeb;
      border-color: #fde68a;

      .pix-icon {
        color: #d97706;
      }
    }
  }

  // Card Payment Section
  .card-payment-section {
    margin-top: 0.5rem;
  }

  .card-instruction {
    text-align: center;
    font-size: 0.75rem;
    color: #4b5563;
    margin-bottom: 0.75rem;
    font-weight: 600;
  }

  // Buttons
  .button-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;

    &.button-group {
      flex-direction: column;
      gap: 0.6rem;

      .btn {
        width: auto;
        min-width: 200px;
        max-width: 90%;
        padding: 0.5rem 1.5rem;
        font-size: 0.8rem;
        border-radius: 8px;
        line-height: 1.2;

        .btn-text-content {
          white-space: pre-line;
          display: block;
        }

        &.btn-disabled-visual {
          opacity: 0.75;
          cursor: not-allowed;
        }
      }
    }
  }

  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    20%,
    60% {
      transform: translateX(-6px);
    }
    40%,
    80% {
      transform: translateX(6px);
    }
  }

  @media (max-width: 576px) {
    .checkout-summary {
      padding: 0.25rem;
    }

    .summary-details {
      padding: 1rem 0;
    }

    .amount-value {
      font-size: 2.5rem;
    }
  }
</style>
